// Constants of the product editor: tab set, content-type field visibility, Stripe minimums.

export const EPublishStatus = {
  // must be kept in sync with the API catalog lib/catalog-store EPublishStatus
  Draft: 0,
  InReview: 1,
  Published: 2,
  Rejected: 3,
  // sent back by a reviewer with what to change: the vendor's turn, then it is sent again
  ReviewRequired: 4,
}

export const publishStateTexts = ["Draft", "In Review", "Approved", "Rejected", "Review Required"]

export const COST_MULTIPLIER = 100

// Localized fields of a Product (they hold framework localization keys)
import { usesField } from "../../../Storefront/pages/page-templates"

export const LOCALIZED_FIELDS = ["name", "author_display_name", "description", "thumb", "square", "cover", "original", "bookTrailer", "fields"]
export const AUTO_TRANSLATION_FIELDS = ["name", "author_display_name", "description"]
export const CREATOR_FIELDS = ["author", "illustrator", "narrator", "translator"]

// Server managed — never sent back by the editor
export const SERVER_FIELDS = ["_id", "publisher", "created", "updated", "published", "contentDuration", "deliverables", "views", "purchases", "physicalReservedCount", "companyShipping"]

// Stripe minimum amounts in cents (to avoid currency conversion issues)
// https://stripe.com/docs/currencies#minimum-and-maximum-charge-amounts
const STRIPE_MINIMUM_AMOUNTS = {
  USD: 50,
  EUR: 50,
  GBP: 30,
  AUD: 50,
  CAD: 50,
  CHF: 50,
  CZK: 1500,
  DKK: 250,
  HKD: 400,
  HUF: 17500,
  INR: 50,
  JPY: 50,
  MXN: 1000,
  NOK: 300,
  NZD: 50,
  PLN: 200,
  RON: 200,
  SEK: 300,
  SGD: 50,
}

export const getMinimumAmount = (currencyCode) => STRIPE_MINIMUM_AMOUNTS[currencyCode?.toUpperCase()] || 50
export const formatMinimumAmount = (currencyCode) => (getMinimumAmount(currencyCode) / 100).toFixed(2)
export const constructRealPrice = (price, currency) => `${currency}_${price}`

// a function that extracts youtube id from url
export const getYoutubeId = (url) => {
  if (!url) return ""
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[7].length === 11 ? match[7] : null
}

// The rows a store page may show besides the product's own fields (Store Page
// tab); the fields — pages, ISBN, whatever the product carries — are listed
// from the product itself (storePageFieldsOf)
export const STORE_PAGE_FIELDS = [
  { key: "availableForPurchase", label: "Available for purchase", defaultValue: "product.availableForPurchase" },
  { key: "publisher", label: "Published by", defaultValue: "product.publisher" },
  { key: "publishDate", label: "Publish date", defaultValue: "product.created" },
  { key: "author", label: "Author", defaultValue: "product.author" },
  { key: "translator", label: "Translator", defaultValue: "product.translator" },
  { key: "illustrator", label: "Illustrator", defaultValue: "product.illustrator" },
  { key: "narrator", label: "Narrator", defaultValue: "product.narrator" },
  { key: "deliverableTypes", label: "Deliverable Types", defaultValue: "deliverables[].deliverableType" },
  { key: "deliverableCount", label: "Deliverable Count", defaultValue: "deliverables.length" },
  { key: "supportedPlatforms", label: "Supported Platforms", defaultValue: "bookSettings.platforms" },
]

/**
 * Everything a product's page may show: the rows above, then its fields —
 * the content type's presets (labelled) and the keys the product carries in
 * any language.
 */
export const storePageFieldsOf = (contentType, fieldsByLanguage) => {
  const presets = contentType?.fieldPresets && typeof contentType.fieldPresets === "object" ? contentType.fieldPresets : {}
  const keys = new Set(Object.keys(presets))
  for (const map of Object.values(fieldsByLanguage || {})) if (map && typeof map === "object") for (const key of Object.keys(map)) keys.add(key)
  const own = [...keys].map((key) => ({ key, label: presets[key] || key, defaultValue: `fields.${key}` }))
  const taken = new Set(own.map((f) => f.key))
  return [...STORE_PAGE_FIELDS.filter((f) => !taken.has(f.key)), ...own]
}

// Which dedicated fields the editor shows: what the product's page template renders (its required fields and
// the optional ones its type switched on), what every product has and what its kind needs to be sold — one
// definition, the storefront's (Storefront/pages/page-templates)
export const fieldVisible = (contentType, fieldName) => usesField(contentType, fieldName)

export const tabVisibility = (contentType) => {
  const show = (f) => fieldVisible(contentType, f)
  return {
    information: true,
    authors: show("author") || show("author_display_name") || show("translator") || show("illustrator") || show("narrator"),
    images: show("square") || show("thumb") || show("cover") || show("original") || show("screenshots"),
    trailer: show("bookTrailer"),
    // a printed book's look-inside PDF (the type's extension list decides once it has one)
    bookPreview: contentType?.bookPreview === true,
    // datasheets and specifications (the type's extension list decides once it has one)
    documents: show("documents"),
    pricing: true,
    store: true,
    // the tab also holds the store product's own `enabled` switch, so it is
    // shown for every product, not only for the ones that count stock
    availability: true,
    shipping: show("weight") || show("free_shipping"),
    referral: true,
    // the publish workflow is every catalog product's, whatever it sells
    adminReview: true,
    categories: show("category"),
    // the product's type and tags (the type's extension list decides once it has one)
    tags: true,
    languages: show("languages"),
    // what the player plays: pages, chapters, tracks (a download has its files instead)
    content: !!contentType?.player && !contentType?.isDigitalDownload,
    deliverables: contentType?.isDigitalDownload === true,
    keys: contentType?.isLicenseKey === true,
    platforms: contentType?.isDigitalDownload === true || contentType?.isLicenseKey === true,
    discounts: true,
  }
}

// Page editor vocab (Unity product player)
export const LAYOUTS = ["web", "phone", "tablet"]
export const TRANSITIONS = ["Instant", "Fade", "Slide", "Zoom"]
export const TEXT_FRAMES = ["comics", "none", "box", "rounded"]
export const ALIGNMENTS = ["Left", "Center", "Right"]
export const REVEAL_EFFECTS = ["Instant", "Typewriter", "Fade"]
